import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  IconButton,
  Link,
  Stack,
  Toolbar,
  Typography,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Menu as MenuIcon,
  ArrowForward as ArrowForwardIcon,
  GetApp as GetAppIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon
} from '@mui/icons-material';
import logo from './logo.svg';

export default function LandingPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const services = [
    { title: 'Electrician', icon: '🔌' },
    { title: 'Mechanical Technician', icon: '🛠️' },
    { title: 'Assembly Line Operators', icon: '🔧' },
    { title: 'Data Collection', icon: '💡' },
    { title: 'Volunteer', icon: '🐾' },
    { title: 'Bouncers', icon: '🧺' },
    { title: 'Packaging', icon: '📦' },
    { title: 'Loading', icon: '🎚️' }
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Modern AppBar */}
      <AppBar position="fixed" color="inherit" elevation={1}>
        <Container maxWidth="xl">
          <Toolbar disableGutters>
            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: { xs: 1, md: 0 } }}>
              <img src={logo} alt="Go Gig" style={{ height: 40, marginRight: 12 }} />
              <Typography
                variant="h6"
                noWrap
                sx={{
                  fontWeight: 700,
                  color: 'primary.main',
                  textDecoration: 'none',
                  letterSpacing: '.1rem'
                }}
              >
                Go Gig
              </Typography>
            </Box>

            {isMobile ? (
              <IconButton
                size="large"
                edge="end"
                color="inherit"
                aria-label="menu"
              >
                <MenuIcon />
              </IconButton>
            ) : (
              <>
                <Box sx={{ flexGrow: 1, display: 'flex', gap: 4, ml: 4 }}>
                  {['Home', 'Services', 'Features', 'How It Works'].map((page) => (
                    <Button
                      key={page}
                      sx={{
                        color: 'text.primary',
                        fontWeight: 600,
                        '&:hover': { color: 'primary.main' }
                      }}
                    >
                      {page}
                    </Button>
                  ))}
                </Box>

                <Stack direction="row" spacing={2}>
                  <Button
                    variant="contained"
                    onClick={() => navigate('/welcome')}
                    sx={{
                      background: (theme) => `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                      color: 'white',
                      fontWeight: 600
                    }}
                  >
                    Go To App
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/login?guest=1')}
                    sx={{ fontWeight: 600 }}
                  >
                    Login as Guest
                  </Button>
                </Stack>
              </>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Hero Section */}
      <Box
        sx={{
          pt: { xs: 12, md: 20 },
          pb: { xs: 8, md: 12 },
          background: (theme) => `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
          color: 'white',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.5rem', md: '4rem' },
                  fontWeight: 800,
                  mb: 2,
                  textShadow: '0 2px 10px rgba(0,0,0,0.1)'
                }}
              >
                We're defining a new way of working
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  mb: 4,
                  opacity: 0.9,
                  maxWidth: 500
                }}
              >
                Empowering apprenticeships and gig work with technology, opportunity, and community.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/welcome')}
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    bgcolor: 'white',
                    color: 'primary.main',
                    fontWeight: 600,
                    '&:hover': {
                      bgcolor: 'grey.100'
                    }
                  }}
                >
                  Get Started
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/login?guest=1')}
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: 'grey.100',
                      bgcolor: 'rgba(255,255,255,0.1)'
                    }
                  }}
                >
                  Try as Guest
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'block' } }}>
              <Box
                component="img"
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=800&q=80"
                sx={{
                  width: '100%',
                  maxWidth: 500,
                  borderRadius: 4,
                  boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
                  transform: 'perspective(1000px) rotateY(-10deg)',
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Services Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            align="center"
            sx={{
              mb: 1,
              fontWeight: 800,
              background: (theme) => `-webkit-linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            Our Services
          </Typography>
          <Typography
            variant="h6"
            align="center"
            color="text.secondary"
            sx={{ mb: 8, maxWidth: 600, mx: 'auto' }}
          >
            Discover opportunities that match your skills and aspirations
          </Typography>

          <Grid container spacing={3}>
            {services.map((service) => (
              <Grid item xs={12} sm={6} md={3} key={service.title}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    p: 3,
                    transition: '0.3s',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: (theme) => `0 20px 40px ${theme.palette.primary.main}15`
                    }
                  }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                      background: (theme) => `linear-gradient(45deg, ${theme.palette.primary.main}20 30%, ${theme.palette.secondary.main}20 90%)`,
                      fontSize: '2.5rem'
                    }}
                  >
                    {service.icon}
                  </Box>
                  <Typography
                    variant="h6"
                    align="center"
                    sx={{ fontWeight: 600 }}
                  >
                    {service.title}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Download Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: (theme) => `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.secondary.dark} 90%)`,
          color: 'white'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h3" sx={{ fontWeight: 800, mb: 2 }}>
                Get the Gogig App
              </Typography>
              <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
                Access opportunities and manage your gigs on the go with our mobile app
              </Typography>
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  startIcon={<GetAppIcon />}
                  sx={{
                    bgcolor: 'white',
                    color: 'primary.main',
                    '&:hover': { bgcolor: 'grey.100' }
                  }}
                >
                  App Store
                </Button>
                <Button
                  variant="contained"
                  startIcon={<GetAppIcon />}
                  sx={{
                    bgcolor: 'white',
                    color: 'primary.main',
                    '&:hover': { bgcolor: 'grey.100' }
                  }}
                >
                  Google Play
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                component="img"
                src="https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&w=800&q=80"
                sx={{
                  width: '100%',
                  maxWidth: 400,
                  borderRadius: 4,
                  boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
                  mx: 'auto',
                  display: 'block'
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ bgcolor: 'background.paper', py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <img src={logo} alt="Go Gig" style={{ height: 32, marginRight: 8 }} />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Go Gig
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Empowering the future of work through technology and community.
                </Typography>
                <Stack direction="row" spacing={1}>
                  <IconButton color="primary" size="small">
                    <FacebookIcon />
                  </IconButton>
                  <IconButton color="primary" size="small">
                    <TwitterIcon />
                  </IconButton>
                  <IconButton color="primary" size="small">
                    <LinkedInIcon />
                  </IconButton>
                </Stack>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Company
              </Typography>
              <Stack spacing={1}>
                {['About Us', 'Careers', 'Blog', 'Press'].map((item) => (
                  <Link
                    key={item}
                    href="#"
                    underline="hover"
                    color="text.secondary"
                    sx={{ '&:hover': { color: 'primary.main' } }}
                  >
                    {item}
                  </Link>
                ))}
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Resources
              </Typography>
              <Stack spacing={1}>
                {['Help Center', 'Community', 'Partners', 'Status'].map((item) => (
                  <Link
                    key={item}
                    href="#"
                    underline="hover"
                    color="text.secondary"
                    sx={{ '&:hover': { color: 'primary.main' } }}
                  >
                    {item}
                  </Link>
                ))}
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Legal
              </Typography>
              <Stack spacing={1}>
                {['Privacy', 'Terms', 'Security', 'Complaints'].map((item) => (
                  <Link
                    key={item}
                    href="#"
                    underline="hover"
                    color="text.secondary"
                    sx={{ '&:hover': { color: 'primary.main' } }}
                  >
                    {item}
                  </Link>
                ))}
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Contact
              </Typography>
              <Stack spacing={1}>
                <Typography variant="body2" color="text.secondary">
                  contact.gogig@gmail.com
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  +91 11693 12165
                </Typography>
              </Stack>
            </Grid>
          </Grid>
          <Box
            sx={{
              mt: 8,
              pt: 4,
              borderTop: 1,
              borderColor: 'divider',
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              gap: 2
            }}
          >
            <Typography variant="body2" color="text.secondary">
              © {new Date().getFullYear()} Go Gig. All rights reserved.
            </Typography>
            <Stack direction="row" spacing={3}>
              <Link href="#" underline="hover" color="text.secondary" variant="body2">
                Privacy Policy
              </Link>
              <Link href="#" underline="hover" color="text.secondary" variant="body2">
                Terms of Service
              </Link>
              <Link href="#" underline="hover" color="text.secondary" variant="body2">
                Cookie Policy
              </Link>
            </Stack>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}